import { useState, useEffect } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff } from "lucide-react";

function SortableRow({ section, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style}
         className={`flex items-center justify-between p-3 rounded-xl border ${isDragging ? "border-sky-400 bg-sky-50" : "border-neutral-200 bg-white"}`}
         data-testid={`sortable-section-${section.key}`}>
      <div className="flex items-center gap-3">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 hover:bg-neutral-100 rounded" data-testid={`drag-handle-${section.key}`}>
          <GripVertical className="w-4 h-4 text-neutral-400" />
        </button>
        <div className="font-semibold">{section.label}</div>
        <div className="text-xs text-neutral-400">({section.key})</div>
      </div>
      <button onClick={onToggle}
              className={`p-1.5 rounded ${section.visible ? "bg-sky-100 text-sky-700" : "bg-neutral-100 text-neutral-500"}`}
              data-testid={`section-toggle-${section.key}`}>
        {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function SortableSections({ sections, onChange }) {
  const [items, setItems] = useState(sections || []);
  useEffect(() => { setItems(sections || []); }, [sections]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((s) => s.key === active.id);
    const newIndex = items.findIndex((s) => s.key === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    onChange(next);
  };

  const toggle = (idx) => {
    const next = items.map((s, i) => i === idx ? { ...s, visible: !s.visible } : s);
    setItems(next);
    onChange(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((s) => s.key)} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid="sortable-sections">
          {items.map((s, i) => (
            <SortableRow key={s.key} section={s} onToggle={() => toggle(i)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
